import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, resolveDynamicComponent as _resolveDynamicComponent, toHandlers as _toHandlers, mergeProps as _mergeProps, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "container" }
const _hoisted_2 = { class: "tabs-container scrollbar-container" }
const _hoisted_3 = ["onClick"]

import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from "axios";
import Page from "@/components/layout/Page.vue";
import Loader from "@/components/Loader.vue";
import Breadcrumbs from "@/components/shared/Breadcrumbs.vue";
import Header from "@/components/loan/Header.vue";
import DetailsTab from "@/components/loan/tabs/DetailsTab.vue";
import DrawsTab from "@/components/loan/tabs/DrawsTab.vue";
import RefiDisbursementsTab from "@/components/loan/tabs/RefiDisbursementsTab.vue";
import AnnualReviewTab from "@/components/loan/tabs/AnnualReviewTab.vue";
import DocumentsTab from "@/components/loan/tabs/DocumentsTab.vue";
import CommentsTab from "@/components/loan/tabs/CommentsTab.vue";
import { LoanDetails } from "@/models/loans";
import functions from "@/use/functions.js";
import { FormModes } from "@/types/form";

interface TabViewModel {
  name: string;
  identifier: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'Loan',
  setup(__props) {

const route = useRoute();
const router = useRouter();

const dynamicComponentRef = ref<any>(null);

const isLoading = ref<boolean>(true);
const details = ref({} as LoanDetails);
const referenceId = ref<string>();
const documentEntityId = ref<number>();
const loanId = ref<number>();
const tabs = ref<TabViewModel[]>([]);
const activeTab = ref<string>("details");

const tabComponents = {
  details: DetailsTab,
  draws: DrawsTab,
  "refi-disbursements": RefiDisbursementsTab,
  "annual-review": AnnualReviewTab,
  documents: DocumentsTab,
  comments: CommentsTab,
};

const activeTabComponent = computed(
  () => tabComponents[activeTab.value] || null
);

const activeTabListeners = computed(() => {
  switch (activeTab.value) {
    case "draws":
      return {
        studentUpdated: async () => {
          await refreshStudentData();
        },
      };
    default:
      return {};
  }
});
const activeTabProps = computed(() => {
  switch (activeTab.value) {
    case "details":
      return { loanDetails: details.value, onUpdateLoan: getLoanAsync };
    case "draws":
      return {
        loanId: loanId.value,
        loanDetails: details.value,
        availableToDrawAmount: details.value?.loanAmount,
        programTypeId: details.value?.loan?.programTypeId,
        productTypeId: details.value?.loan?.productType,
        lenderId: details.value?.loan?.lenderId,
        minimumDrawAmount: details.value?.minimumDrawAmount,
        onDrawsChanged: getLoanAsync,
      };
    case "refi-disbursements":
      return {
        loanId: loanId.value,
        lenderId: details.value?.loan?.lenderId,
        onPayoffsChanged: getLoanAsync,
      };
    case "documents":
      return {
        documentEntityId: details.value?.documentEntityId,
        loanId: details.value?.loanId,
        borrowers: details.value?.borrowers,
        referenceId: details.value?.loan.referenceId
      };
    case "annual-review":
      return {
        loanId: loanId.value,
        lenderId: details.value.loan.lenderId,
        programTypeId: details.value.loan.programTypeId,
        productTypeId: details.value.loan.productType,
        referenceId: details.value.loan.referenceId,
        applicationUrl: details.value.applicationUrl,
        annualReviews: details.value.annualReviews,
      };
    case "comments":
      return {
        loanId: loanId.value,
      };
    default:
      return {};
  }
});

const tabParameterMap: Record<string, string[]> = {
  draws: ["drawId", "disbursementId", "studentDetailsExpanded"],
  "refi-disbursements": ["payoffSortKey", "payoffSortOrder"],
  documents: ["documentsSortKey", "documentsSortOrder"],
  "annual-review": [
    "annualReviewId",
    "alertsSortKey",
    "alertsSortOrder",
    "knockoutsSortKey",
    "knockoutsSortOrder",
  ],
};

function selectTab(tabIdentifier: string) {
  activeTab.value = tabIdentifier;

  const allowedParams = tabParameterMap[tabIdentifier] || [];

  const newQuery: Record<string, string | undefined> = {};

  // persist params for same tab, otherwise clear
  for (const key of Object.keys(route.query)) {
    if (allowedParams.includes(key)) {
      newQuery[key] = route.query[key] as string;
    }
  }

  router.replace({
    query: newQuery,
    hash: `#${tabIdentifier}`,
  });
}

async function refreshStudentData() {
  try {
    const response = await axios(
      `/api/loanservice/loans/details/ref/${referenceId.value}`
    );

    details.value = { ...details.value, student: response.data.student };
    loanId.value = response.data.loan.id;
    documentEntityId.value = response.data.entityId;
    tabs.value = response.data.tabs;

    if (
      dynamicComponentRef.value &&
      dynamicComponentRef.value?.studentFormRef
    ) {
      dynamicComponentRef.value?.studentFormRef?.changeMode(FormModes.view);
    }

    functions.closeModal();
  } catch (error) {
    router.push("/page-not-found");
  }
}

async function getLoanAsync() {
  try {
    const response = await axios(
      `/api/loanservice/loans/details/ref/${referenceId.value}`
    );

    details.value = response.data;
    loanId.value = response.data.loan.id;
    documentEntityId.value = response.data.entityId;
    tabs.value = response.data.tabs;
  } catch (error) {
    router.push("/page-not-found");
  }
}

onMounted(async () => {
  const id = route.params.referenceId;

  referenceId.value = String(id);

  await getLoanAsync();

  const initialTab = window.location.hash.replace("#", "");

  if (initialTab && tabs.value.some((tab) => tab.identifier === initialTab)) {
    selectTab(initialTab);
  }

  functions.logPageView({
    id: referenceId.value,
    page: "loans",
    category: "Loan",
    timestamp: Date.now(),
    firstName: details.value.borrowers[0]?.firstName,
    lastName: details.value.borrowers[0]?.lastName,
    name: details.value.borrowers[0]?.fullName,
    lenderId: details.value.loan?.lenderId,
    lenderName: details.value.lenderName,
    programName: details.value.programName,
  });

  isLoading.value = false;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    (isLoading.value)
      ? (_openBlock(), _createBlock(Loader, { key: 0 }))
      : (_openBlock(), _createBlock(Page, { key: 1 }, {
          default: _withCtx(() => [
            _createVNode(Breadcrumbs, { "root-path": "/loans" }),
            _createElementVNode("div", _hoisted_1, [
              _createVNode(Header, {
                "reference-id": referenceId.value,
                "loan-details": details.value
              }, null, 8, ["reference-id", "loan-details"]),
              _createElementVNode("div", _hoisted_2, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tabs.value, (tab) => {
                  return (_openBlock(), _createElementBlock("button", {
                    key: tab.identifier,
                    class: _normalizeClass({
              'tab-button': true,
              active: activeTab.value === tab.identifier,
            }),
                    onClick: ($event: any) => (selectTab(tab.identifier))
                  }, _toDisplayString(tab.name), 11, _hoisted_3))
                }), 128))
              ]),
              (_openBlock(), _createBlock(_resolveDynamicComponent(activeTabComponent.value), _mergeProps({
                ref_key: "dynamicComponentRef",
                ref: dynamicComponentRef
              }, activeTabProps.value, _toHandlers(activeTabListeners.value), { class: "tab-content" }), null, 16))
            ])
          ]),
          _: 1
        }))
  ]))
}
}

})